import {
  Bar,
  DatafeedConfiguration,
  IBasicDataFeed,
  LibrarySymbolInfo,
} from "tradingView";
import { initDatafeedParams } from "./tradeView";
import {
  getTokenSymbol,
  queryDevOptions,
  querySptKline,
  queryTronKlineData,
} from "../http";
import dayjs from "dayjs";
import { TransSub } from "./useTVChart";
import { add, groupBy } from "lodash-es";
import { Subscription } from "rxjs";
import { colors } from "./color";
import { numFormatter, postWindowMessage } from "./utils";
import { GloablMessageSub, GlobalMessageType, PostMessageType } from "../rxjs";
import { DEV_ORIGIN } from "../App";

interface QueryDevActionsItem {
  type: "buy" | "sell";
  dex: "pump" | "raydium";
  txId: string;
  //本币数量
  baseAmount: string;
  //代币数量
  tokenAmount: string;
  //13位时间戳
  timestamp: number;
}

interface BirdEyeData {
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
  v_usd: number;
  unixTime: number;
  unix_time: number;
}

interface TronKData {
  result: Array<{
    max: number;
    min: number;
    open: number;
    price: number;
    volume: string;
    volumeInUsd: string;
    block: { time: string };
    unix: number;
  }>;
}

const configurationData = {
  supported_resolutions: ["1S", 1, 5, 15, 30, 60, 240, "1D", "1W"],
  supports_marks: true,
  supports_timescale_marks: true,
} as DatafeedConfiguration;

const resolutionMap: Record<string, string> = {
  "1S": "1s",
  "1": "1m",
  "3": "3m",
  "5": "5m",
  "15": "15m",
  "30": "30m",
  "60": "1H",
  "120": "2H",
  "240": "4H",
  "360": "6H",
  "480": "8H",
  "720": "12H",
  "1D": "1D",
  "3D": "3D",
  "1W": "1W",
  "1M": "1M",
};
const tronResolutionMap: Record<
  string,
  {
    count: number;
    interval:
      | "seconds"
      | "minutes"
      | "hours"
      | "days"
      | "weeks"
      | "months"
      | "years";
  }
> = {
  "1S": { count: 1, interval: "seconds" },
  "1": { count: 1, interval: "minutes" },
  "3": { count: 3, interval: "minutes" },
  "5": { count: 5, interval: "minutes" },
  "15": { count: 15, interval: "minutes" },
  "30": { count: 30, interval: "minutes" },
  "60": { count: 1, interval: "hours" },
  "120": { count: 2, interval: "hours" },
  "240": { count: 4, interval: "hours" },
  "360": { count: 6, interval: "hours" },
  "480": { count: 8, interval: "hours" },
  "720": { count: 12, interval: "hours" },
  "1D": { count: 1, interval: "days" },
  "3D": { count: 3, interval: "days" },
  "1W": { count: 1, interval: "weeks" },
  "1M": { count: 1, interval: "months" },
};
export const gapNumber: { [key: string]: number } = {
  "1S": 1 / 60,
  "1": 1,
  "3": 3,
  "5": 5,
  "15": 15,
  "30": 30,
  "60": 60,
  "120": 120,
  "240": 240,
  "360": 360,
  "480": 480,
  "720": 720,
  "1D": 1440,
  "3D": 1440 * 3,
  "1W": 1440 * 7,
};

export function getNearTime(u: number, timeGapNumber: number) {
  let unix = u;
  const gapSeconds = timeGapNumber * 60;
  // 1w单独处理
  if (timeGapNumber === 1440 * 7) {
    unix =
      unix - (new Date().getUTCDay() - 1) * 1440 * 60 - (unix % (1440 * 60));
  } else {
    unix = unix - (unix % gapSeconds);
  }

  return unix * 1000;
}

const sleep = (delay: number) =>
  new Promise((resolve) => setTimeout(resolve, delay));

function formatTronKData(
  data: TronKData,
  from: number,
  to: number,
  timeGapNumber: number
): Bar[] {
  const dataMap: Record<number, Bar> = {};
  let validData: Bar | undefined;
  let lastKey: number | undefined = undefined;
  if (!Array.isArray(data.result)) {
    return [];
  }

  data.result.forEach((item) => {
    item.unix = dayjs.tz(item.block.time).unix();
    const nearTimeKey = getNearTime(item.unix, timeGapNumber);
    const exists = dataMap[nearTimeKey];
    const last = lastKey ? dataMap[lastKey] : undefined;
    dataMap[nearTimeKey] = {
      time: nearTimeKey,
      open: exists?.open || last?.close || item.open,
      high: Math.max(exists?.high ?? item.price, item.price),
      low: Math.min(exists?.low ?? item.price, item.price),
      close: item.price,
      volume: (exists?.volume ?? 0) + Number(item.volumeInUsd),
    };
    if (!lastKey || lastKey !== nearTimeKey) {
      lastKey = nearTimeKey;
    }
  });
  let flag = Math.min(from, getNearTime(data.result[0].unix, timeGapNumber));
  let bars: Bar[] = [];
  const endTime = Math.max(
    to,
    getNearTime(data.result.at(-1)!.unix, timeGapNumber)
  );
  validData = undefined;

  while (flag <= endTime) {
    const timeKey = getNearTime(flag, timeGapNumber);
    if (dataMap[timeKey]) {
      bars.push(dataMap[timeKey]);
      validData = dataMap[timeKey];
    } else {
      bars.push({
        time: timeKey,
        open: validData?.close,
        high: validData?.close,
        low: validData?.close,
        close: validData?.close,
        volume: 0,
      } as any);
    }
    flag += timeGapNumber * 1000 * 60;
  }
  return bars;
}

/**
 * @description 定义td获取数据的方式和更新数据
 */
export function initDatafeed({
  symbol = " ",
  address,
  priceScale = 10,
  tvWidgetRef,
  arrow = "true",
  chain = "Solana",
  upColor,
  downColor,
}: initDatafeedParams): IBasicDataFeed {
  const unSubMap = new Map<string, Subscription[]>();
  // 根据不同的resolution区分lastbar，避免手动取消监听，避免lastbar串了导致数据不够连续
  const lastBarMap = new Map<string, Bar | null>();
  const firstBarMap = new Map<string, Bar | null>();

  const updateArrow = async (newBar: Bar, up: boolean) => {
    const flashGap = 50;
    try {
      if (tvWidgetRef?.current && document.visibilityState === "visible") {
        let count = 5;
        for (let i = 0; i < count; i++) {
          let entityId = tvWidgetRef.current.chart().createShape(
            {
              channel: up ? "low" : "high",
              time: newBar.time,
            },
            {
              shape: up ? "arrow_up" : "arrow_down",
              overrides: {
                arrowColor: up ? upColor : downColor,
              },
              zOrder: "top",
            }
          );
          await sleep(flashGap);
          entityId && tvWidgetRef?.current?.chart().removeEntity(entityId);
          await sleep(flashGap);
        }
      }
    } catch {}
  };

  return {
    onReady: async (callback) => {
      setTimeout(() => {
        callback(configurationData);
      });
    },
    getMarks: async (
      symbolInfo,
      startDate,
      endDate,
      onDataCallback,
      resolution
    ) => {
      try {
        if (chain === "Solana") {
          const res = await queryDevOptions({ chain, token: address });
          const resJson = await res.json();
          if (Array.isArray(resJson?.result)) {
            const devDatas = (resJson.result as QueryDevActionsItem[]).map(
              (item) => ({
                id: item.txId,
                time: dayjs(item.timestamp).set("second", 0).unix(),
                color: {
                  /** Border color */
                  border: "transparent",
                  /** Background color */
                  background: item.type === "buy" ? upColor : downColor,
                },
                text: `${item.type === "buy" ? "Dev Bought" : "Dev Sold"} ${numFormatter(Number(item.tokenAmount))}(${numFormatter(Number(item.baseAmount))} ${chain === "Solana" ? "SOL" : "TRX"})\n ${dayjs(item.timestamp).format("YYYY-MM-DD HH:mm:ss")}`,
                label: item.type === "buy" ? "DB" : "DS",
                labelFontColor: "black",
                minSize: 20,
              })
            );
            onDataCallback(devDatas as any[]);
            GloablMessageSub.next({ type: GlobalMessageType.GET_MAKRS });
          }
        }
      } catch {}
    },
    searchSymbols: () => {},
    resolveSymbol: async (symbolName, onSymbolResolvedCallback) => {
      let data = {
        name: symbol,
        full_name: symbol,
        exchange: "",
        listed_exchange: "",
        minmov: 1,
        // day
        has_daily: true,
        has_weekly_and_monthly: true,
        // minutes
        has_intraday: true,
        description: symbol,
        type: "crypto",
        session: "24x7",
        pricescale: Math.pow(10, priceScale),
        // 秒图
        has_seconds: true,
        seconds_multipliers: ["1"],
        ticker: symbol,
        has_empty_bars: false,
        supported_resolutions: ["1S", 1, 5, 15, 30, 60, 240, "1D", "1W"],
        timezone: dayjs.tz.guess(),
      } as LibrarySymbolInfo;

      setTimeout(() => {
        onSymbolResolvedCallback(data);
      });
      try {
        const res = await getTokenSymbol({
          token: address,
        });
        const resJson = await res.json();
        GloablMessageSub.next({
          type: GlobalMessageType.GET_TOKEN_INFO,
          data: resJson,
        });
        const value = resJson.result.symbol || "Undefined";
        data.name = value;
        data.full_name = value;
        data.description = value;
        data.ticker = value;
        onSymbolResolvedCallback(data);
      } catch (err) {
        console.log(err);
      }
    },
    getBars: async (
      symbolInfo,
      resolution,
      periodParams,
      onHistoryCallback,
      onErrorCallback
    ) => {
      const lastBar = lastBarMap.get(resolution);
      try {
        let { from, to, firstDataRequest } = periodParams;

        let bars: Bar[] = [];
        if (from <= 0 || to <= 0) {
          onHistoryCallback([], { noData: true });
          return;
        }
        if (chain.toLowerCase() === "solana") {
          let solanaBars: BirdEyeData[] = [];
          // 默认走自己的k线数据
          const response = await querySptKline({
            address,
            type: resolutionMap[resolution],
            time_from: from,
            time_to: firstDataRequest ? dayjs().unix() : to,
          });
          const resData = await response.json();
          if (resData.code !== 0 || !Array.isArray(resData.result?.items)) {
            throw new TypeError();
          }
          solanaBars = resData.result?.items;

          bars = solanaBars.map((item: BirdEyeData) => ({
            time: (item.unix_time || item.unixTime) * 1000, // 将秒时间戳转换为毫秒时间戳
            open: item.o, // 对应开盘价
            high: item.h, // 对应最高价
            low: item.l, // 对应最低价
            close: item.c, // 对应收盘价
            volume: item.v_usd || item.v,
          }));
          if (resolution === "1S") {
            // 秒线图前面的数据openclose会有问题所以手动处理一下
            const firstBar = firstBarMap.get(resolution);
            const lastdata = bars.at(-1);
            if (
              firstBar &&
              lastdata &&
              firstBar.time - 1000 === lastdata?.time &&
              lastdata.close !== firstBar.open
            ) {
              lastdata.close = firstBar.open;
            }
            if (!firstBar || bars[0].time < (firstBar?.time ?? 0)) {
              firstBarMap.set(resolution, bars[0]);
            }
          }
        } else {
          if (resolution === "1S") {
            // message.warning("暂不支持该代币1S历史数据");
            lastBarMap.set(resolution, null);
            onHistoryCallback([], { noData: true });
            return;
          }
          const response = await queryTronKlineData({
            token: address,
            ...tronResolutionMap[resolution],
            from: from * 1000,
            to: to * 1000,
          });
          const resData = await response.json();
          bars = formatTronKData(
            resData as TronKData,
            from * 1000,
            to * 1000,
            gapNumber[resolution]
          );
        }
        if (!bars.length) {
          onHistoryCallback(bars, { noData: true });
          return;
        }

        const last = bars.at(-1) || null;
        if (!lastBar || (last?.time || 0) > (lastBar?.time || 0)) {
          lastBarMap.set(resolution, last);
        }
        if (firstDataRequest && last) {
          postWindowMessage({
            type: PostMessageType.GET_KLINE_DATA,
            data: last,
            address,
          });
        }
        onHistoryCallback(bars, { noData: !bars.length });
      } catch (err: any) {
        onHistoryCallback([], { noData: true });
      }
    },
    subscribeBars: (
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscriberUID,
      onResetCacheNeededCallback
    ) => {
      const subscrip = TransSub.subscribe((data) => {
        const lastBar = lastBarMap.get(resolution);
        if (!Array.isArray(data) || !data.length) return;

        let formatedDateData = data
          .filter((i) => i.token === address)
          .map((item) => {
            return {
              ...item,
              timeKey: getNearTime(item.unix, gapNumber[resolution]),
            };
          });
        if (lastBar) {
          formatedDateData = formatedDateData.filter(
            (item) => dayjs(item.timedate).valueOf() >= lastBar.time
          );
        }
        const groups = groupBy(formatedDateData, "timeKey");
        Object.keys(groups).forEach((key) => {
          const datas = groups[key];

          const sameKey = lastBar?.time === Number(key);
          const newBar = {
            time: Number(key),
            open: sameKey
              ? lastBar?.open || 0
              : lastBar?.close || Number(datas[datas.length - 1].priceUsdt),
            high: Math.max(
              sameKey ? lastBar?.high || 0 : 0,
              ...datas.map((i) => Number(i.priceUsdt))
            ),
            low: Math.min(
              sameKey ? lastBar?.low || 0 : Number(datas[0].priceUsdt),
              ...datas.map((i) => Number(i.priceUsdt))
            ),
            close: Number(datas[0].priceUsdt),
            volume: datas.reduce(
              (pre, cur) =>
                pre + Number(cur.tokenAmount) * Number(cur.priceUsdt),
              sameKey ? lastBar?.volume || 0 : 0
            ),
          };
          if (arrow === "true") {
            updateArrow(newBar, newBar.close > (lastBar?.close ?? 0));
          }
          onRealtimeCallback(newBar);
          lastBarMap.set(resolution, newBar);
        });
      });
      // 某些出了bug的情况下会sub两次然后因为sub了两次会unsub一次
      // 但是由于subscriberUID相同，unsub之后就不会有推送数据了
      // 改为sub[]存储，
      unSubMap.set(
        subscriberUID,
        (unSubMap.get(subscriberUID) || [])?.concat(subscrip)
      );
    },
    unsubscribeBars: (subscriberUID) => {
      const subs = unSubMap.get(subscriberUID) || [];
      if (subs.length) {
        subs.at(0)?.unsubscribe();
        subs.shift();
        unSubMap.set(subscriberUID, subs);
      }
    },
  };
}
